export const permanentFreeRights = [
  'onboarding_without_account',
  'shift_calendar_and_patterns',
  'allowance_calculation_with_evidence_grade',
  'paycheck_reconciliation',
  'evidence_report_and_pdf',
  'annual_basic_statistics',
  'local_export_import_backup_and_delete',
] as const;

export type PermanentFreeRight = typeof permanentFreeRights[number];

export const monetizationPolicy = {
  contractVersion: 1,
  productionActivated: false,
  firstValueEvent: 'first_allowance_viewed',
  initialCohort: {
    priceExposureCount: 0,
    purchaseCtaCount: 0,
    purchaseIntentCount: 0,
    adImpressionCount: 0,
  },
  noCardTrial: { enabled: false, cardRequired: false, autoConverts: false },
  freeRightsCanBeRevoked: false,
  paidOffer: {
    visibility: 'post_value_explicit_request_only',
    requiresExplicitUserRequest: true,
    candidatePriceKrw: 14_900,
    priceStatus: 'hypothesis_not_for_display_or_sale',
  },
  ads: { enabled: false, corePlacementCount: 0, rewardedPlacementCount: 0 },
  providers: { payment: false, receipt: false, restore: false, refund: false, reconciliation: false },
} as const;

export interface MonetizationContext {
  firstValueReached: boolean;
  explicitOfferRequested: boolean;
  existingFreeUser: boolean;
  providerReady: boolean;
}

export interface MonetizationAccess {
  freeRights: readonly PermanentFreeRight[];
  priceVisible: false;
  purchaseCtaVisible: false;
  purchaseIntentAllowed: false;
  adAllowed: false;
  offerVisible: false;
  commerceApiAvailable: false;
  reason: 'production_disabled' | 'provider_unverified' | 'before_first_value' | 'explicit_request_required';
}

export function resolveMonetizationAccess(context: MonetizationContext): MonetizationAccess {
  let reason: MonetizationAccess['reason'] = 'production_disabled';
  if (monetizationPolicy.productionActivated) {
    if (!context.providerReady) reason = 'provider_unverified';
    else if (!context.firstValueReached) reason = 'before_first_value';
    else if (!context.explicitOfferRequested) reason = 'explicit_request_required';
  }

  return {
    freeRights: permanentFreeRights,
    priceVisible: false,
    purchaseCtaVisible: false,
    purchaseIntentAllowed: false,
    adAllowed: false,
    offerVisible: false,
    commerceApiAvailable: false,
    reason,
  };
}

export interface EntitlementEvidence {
  serverLedgerActive: boolean;
  receiptVerified: boolean;
  restoreRequested: boolean;
  restoreVerified: boolean;
  refundStatus: 'clear' | 'refunded' | 'unknown';
  reconciliationStatus: 'verified' | 'pending' | 'failed';
}

export type EntitlementEvidenceDecision =
  | { eligible: true; reason: 'verified' }
  | { eligible: false; reason: 'ledger_unverified' | 'receipt_unverified' | 'restore_unverified' | 'refund_unverified' | 'refunded' | 'reconciliation_unverified' };

export function evaluateEntitlementEvidence(evidence: EntitlementEvidence): EntitlementEvidenceDecision {
  if (!evidence.serverLedgerActive) return { eligible: false, reason: 'ledger_unverified' };
  if (!evidence.receiptVerified) return { eligible: false, reason: 'receipt_unverified' };
  if (evidence.restoreRequested && !evidence.restoreVerified) return { eligible: false, reason: 'restore_unverified' };
  if (evidence.refundStatus === 'refunded') return { eligible: false, reason: 'refunded' };
  if (evidence.refundStatus !== 'clear') return { eligible: false, reason: 'refund_unverified' };
  if (evidence.reconciliationStatus !== 'verified') return { eligible: false, reason: 'reconciliation_unverified' };
  return { eligible: true, reason: 'verified' };
}

export type EntitlementDecision =
  | { freeRights: readonly PermanentFreeRight[]; paidEntitlement: false; status: 'disabled' | EntitlementEvidenceDecision['reason'] }
  | { freeRights: readonly PermanentFreeRight[]; paidEntitlement: true; status: 'verified' };

export function resolvePaidEntitlement(evidence: EntitlementEvidence): EntitlementDecision {
  if (!monetizationPolicy.productionActivated) return { freeRights: permanentFreeRights, paidEntitlement: false, status: 'disabled' };
  const decision = evaluateEntitlementEvidence(evidence);
  return decision.eligible
    ? { freeRights: permanentFreeRights, paidEntitlement: true, status: 'verified' }
    : { freeRights: permanentFreeRights, paidEntitlement: false, status: decision.reason };
}

export interface SettlementInput {
  grossKrw: number | null;
  vatKrw: number | null;
  marketFeeKrw: number | null;
  tossFeeKrw: number | null;
  tossFeeVatKrw: number | null;
  refundKrw: number | null;
}

export type SettlementResult =
  | { status: 'data_pending'; netKrw: null }
  | { status: 'failed'; netKrw: null }
  | { status: 'verified'; netKrw: number };

export function reconcileSettlement(input: SettlementInput): SettlementResult {
  const values = Object.values(input);
  if (values.some((value) => value === null)) return { status: 'data_pending', netKrw: null };
  if (values.some((value) => typeof value !== 'number' || !Number.isFinite(value) || value < 0)) return { status: 'failed', netKrw: null };
  const { grossKrw, vatKrw, marketFeeKrw, tossFeeKrw, tossFeeVatKrw, refundKrw } = input as Record<keyof SettlementInput, number>;
  const netKrw = grossKrw - vatKrw - marketFeeKrw - tossFeeKrw - tossFeeVatKrw - refundKrw;
  return netKrw >= 0 ? { status: 'verified', netKrw } : { status: 'failed', netKrw: null };
}

export interface CommercePort {
  channel: 'web' | 'toss';
  purchase(): Promise<'disabled'>;
  verifyReceipt(): Promise<'disabled'>;
  restore(): Promise<'disabled'>;
  refund(): Promise<'disabled'>;
  reconcile(): Promise<'disabled'>;
  requestAd(): Promise<'disabled'>;
}

export function createDisabledCommercePort(channel: CommercePort['channel']): CommercePort {
  const disabled = async (): Promise<'disabled'> => 'disabled';
  return { channel, purchase: disabled, verifyReceipt: disabled, restore: disabled, refund: disabled, reconcile: disabled, requestAd: disabled };
}
